import {
	ReactElement,
	useCallback,
	useState
} from "react";
import {
	KeyBoardListNavigationContext,
	useKeyboardListNavigation,
	useKeyboardListNavigationItem
} from "client/Hooks";
import { Key } from "client/Misc/commonTypes";
import { IconButton } from "../IconButton/IconButton";
import { FontIconName } from "../Icon/Icon";
import styles from "./EmojiPickerTabs.module.less";
import classNames from "classnames";
import { generateTrackingId, setTrackingId } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";

export type EmojiTab = Exclude<EmojiPickerGroup, EmojiPickerGroup.Results>;

type EmojiPickerTabsProps = {
	groups: readonly EmojiTab[];
	activeGroup: EmojiTab | null;
	onTabClick: (group: EmojiTab) => void;
};

export const EmojiPickerTabs = (props: EmojiPickerTabsProps): ReactElement => {
	const {
		groups,
		activeGroup,
		onTabClick
	} = props;

	const [tabbableIndex, setTabbableIndex] = useState<number>(0);

	const { contextData } = useKeyboardListNavigation({
		navigationDirection: "horizontal",
		onItemFocusChange: setTabbableIndex
	});

	return (
		<KeyBoardListNavigationContext.Provider value={contextData}>
			<ul
				{...setTrackingId("emoji-tabs")}
				className={styles.root}
			>
				{groups.map(
					(group, index): ReactElement => (
						<li key={group}>
							<EmojiPickerTab
								group={group}
								isActive={activeGroup === group}
								itemIndex={index}
								isTabbable={tabbableIndex === index}
								onTabClick={onTabClick}
								onTabbableChange={setTabbableIndex}
							/>
						</li>
					)
				)}
			</ul>
		</KeyBoardListNavigationContext.Provider>
	);
};
EmojiPickerTabs.displayName = "EmojiPickerTabs";

type EmojiPickerTabProps = {
	group: EmojiTab;
	isActive: boolean;
	itemIndex: number;
	isTabbable: boolean;
	onTabClick: (group: EmojiTab) => void;
	onTabbableChange: (index: number) => void;
};

const EmojiPickerTab = (props: EmojiPickerTabProps): ReactElement => {
	const {
		group,
		isActive,
		itemIndex,
		isTabbable,
		onTabClick,
		onTabbableChange
	} = props;

	const tabClickHandler = useCallback(
		(): void => {
			onTabbableChange(itemIndex);
			onTabClick(group);
		},
		[
			onTabbableChange,
			itemIndex,
			onTabClick,
			group
		]
	);

	const {
		ref,
		onKeyDown
	} = useKeyboardListNavigationItem({
		itemIndex,
		executeKey: [Key.Enter, Key.Space],
		onExecute: tabClickHandler
	});

	return (
		<div
			className={
				classNames(
					styles.tab,
					{
						[styles.tab_active]: isActive,
					}
				)
			}
		>
			<IconButton
				ref={ref}
				tabIndex={isTabbable ? 0 : -1}
				kind="transparent-colored"
				trackingId={generateTrackingId("emoji-tab", `|${group}`)}
				icon={{
					kind: "font",
					name: emojiPickerGroupIconsMap[group],
					color: isActive ? "primary" : "primary-text",
				}}
				onClick={tabClickHandler}
				onKeyDown={onKeyDown}
				tooltip={localize(emojiPickerGroupTitleMap[group])}
			/>
		</div>
	);
};
EmojiPickerTab.displayName = "EmojiPickerTab";

export enum EmojiPickerGroup {
	SmileysAndEmotion = 1,
	PeopleAndBody = 2,
	AnimalsAndNature = 3,
	FoodAndDrink = 4,
	TravelAndPlaces = 5,
	Activities = 6,
	Objects = 7,
	Symbols = 8,
	Flags = 9,
	Recent = 10,
	Results = 11
};

const emojiPickerGroupIconsMap: Record<EmojiTab, FontIconName> = {
	[EmojiPickerGroup.Recent]: "clock",
	[EmojiPickerGroup.SmileysAndEmotion]: "face-smile",
	[EmojiPickerGroup.PeopleAndBody]: "user",
	[EmojiPickerGroup.AnimalsAndNature]: "paw",
	[EmojiPickerGroup.FoodAndDrink]: "pizza-slice",
	[EmojiPickerGroup.TravelAndPlaces]: "car-side",
	[EmojiPickerGroup.Activities]: "volleyball",
	[EmojiPickerGroup.Objects]: "lightbulb-on",
	[EmojiPickerGroup.Symbols]: "symbols",
	[EmojiPickerGroup.Flags]: "flag"
};

export const emojiPickerGroupTitleMap: Record<EmojiPickerGroup, LocalizationKey> = {
	[EmojiPickerGroup.Recent]: "_Recent",
	[EmojiPickerGroup.SmileysAndEmotion]: "_SmileysAndEmotion",
	[EmojiPickerGroup.PeopleAndBody]: "_PeopleAndBody",
	[EmojiPickerGroup.AnimalsAndNature]: "_AnimalsAndNature",
	[EmojiPickerGroup.FoodAndDrink]: "_FoodAndDrink",
	[EmojiPickerGroup.TravelAndPlaces]: "_TravelAndPlaces",
	[EmojiPickerGroup.Activities]: "_Activities",
	[EmojiPickerGroup.Objects]: "_Objects",
	[EmojiPickerGroup.Symbols]: "_Symbols",
	[EmojiPickerGroup.Flags]: "_Flags",
	[EmojiPickerGroup.Results]: "_Results"
};